// Read gates — module-scoped holds that keep guarded database reads over a set of
// tables PENDING until released, so "this data hasn't landed on-device yet" renders
// as a normal in-flight query (the existing Suspense/loading views) instead of a
// resolved-but-empty read that every screen would have to special-case.
//
// The producer today is the sign-in deck bootstrap: an existing-account sign-in
// wipes the local actor DB and re-bootstraps it from the account's cloud data, so
// any actor-table read between the wipe and the initial remote pull would resolve
// EMPTY (the Learn summary would flash its new-user welcome view at a returning
// user). The bootstrap installs a gate over the actor tables and releases it once
// the initial pull settles; `withWriteClockReadGuard` awaits intersecting gates
// before reading, so those queries simply stay pending — standard React Query
// semantics, no sync-aware branching in components.
//
// A gate is a hold, not a lock: releasing is idempotent, several gates can coexist,
// and reads whose tables no gate covers are untouched.
//
// A gate can never hold forever. Installing one REQUIRES a maximum hold: it is a
// fail-open `boundedPromise`, so a producer that never releases (a crashed
// bootstrap, a forgotten release, a future producer with a bug) holds matching reads
// for at most that long, after which they read whatever the database holds — the
// same outcome the producer's own timeout would give. A waiting read also stops
// waiting when its query is cancelled.

import { hasLength } from "@-/com.npmjs.ts-array-length";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";

type DatabaseReadGate = {
  promise: Promise<void>;
  release: () => void;
  tables: ReadonlySet<string>;
};

const activeGates = new Set<DatabaseReadGate>();

/**
 * The longest one abortable wait on the gates may take. Every producer's max hold is far below
 * this; it bounds the wait itself so the wait needs no trust in the gates it waits on.
 */
const MAX_READ_GATE_WAIT_MS = 5 * 60_000;

/**
 * Install a gate that holds every guarded read depending on any of `tables` until
 * the returned release function runs, or `maxHoldMs` passes — whichever is first.
 * Releasing twice is a no-op. `maxHoldMs` is required: the gate FAILS OPEN at it, so
 * pick the longest the producer's work can legitimately take (the producer should
 * still release as soon as its work settles; this is the guarantee, not the plan).
 */
export function installDatabaseReadGate(
  tables: readonly string[],
  { maxHoldMs, reason }: { maxHoldMs: number; reason: string },
): () => void {
  const gate: DatabaseReadGate = {
    promise: Promise.resolve(),
    release: () => {},
    tables: new Set(tables),
  };
  gate.promise = boundedPromise(
    { deadline: maxHoldMs, onDeadline: "resolve", operation: `Database read gate: ${reason}` },
    (resolve) => {
      gate.release = resolve;
      return () => {
        // Settled by release or by the max hold: either way it no longer blocks anything.
        activeGates.delete(gate);
      };
    },
  );
  activeGates.add(gate);
  return () => {
    activeGates.delete(gate);
    gate.release();
  };
}

/**
 * Resolve once no active gate covers any of `tables`. Immediate when nothing is
 * gated — the steady state, so this is on every guarded read's hot path. Loops
 * because a release can be followed by a fresh install (back-to-back sign-ins)
 * before an awaiting read resumes.
 *
 * Bounded: every gate fails open at its own max hold. `signal` (read lazily, only
 * when a gate actually blocks — reading TanStack's `signal` changes its cancellation
 * semantics) ends the wait early when the query is cancelled, rejecting with the
 * signal's reason like any other aborted read.
 */
export async function waitForDatabaseReadGates(
  tables: readonly string[],
  getSignal?: () => AbortSignal | undefined,
): Promise<void> {
  while (activeGates.size > 0) {
    const blockingGates = [...activeGates].filter((gate) =>
      tables.some((tableName) => gate.tables.has(tableName)),
    );
    if (hasLength(blockingGates, 0)) {
      return;
    }
    const signal = getSignal?.();
    const released = Promise.all(blockingGates.map((gate) => gate.promise));
    if (signal === undefined) {
      await released;
      continue;
    }
    signal.throwIfAborted();
    await boundedPromise<void>(
      {
        // Never the binding bound: every gate already resolves at its own max hold.
        deadline: MAX_READ_GATE_WAIT_MS,
        operation: "Waiting for a database read gate",
      },
      (resolve, reject) => {
        const onAbort = () => {
          reject(signal.reason);
        };
        signal.addEventListener("abort", onAbort, { once: true });
        void released.then(() => {
          resolve();
        });
        return () => {
          signal.removeEventListener("abort", onAbort);
        };
      },
    );
  }
}

/** Test seam: release and drop every active gate. */
export function clearDatabaseReadGates(): void {
  for (const gate of activeGates) {
    gate.release();
  }
  activeGates.clear();
}
